import { Box, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { useQuery } from "@tanstack/react-query";
import html2pdf from "html2pdf.js";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { dashboardKeys, inventoryViewKeys } from "../../../../../api/queryKeys";
import LoadingIcon from "../../../../../components/LoadingIcon";
import {
  FILE_EXTENSIONS,
  generateFileName,
} from "../../../../../hooks/useFileNaming";
import CustomLegendItem from "../../../mssp_widgets/assets_breakdown/CustomLegendItem";
import {
  createPdfConfig,
  createPdfContainer,
  createPdfHeader,
  measureContentForPdf,
} from "../../utils/commonWidgetUtils";
import WidgetWrapper from "../../WidgetWrapper";

const CustomWidget = forwardRef(({ widget }, ref) => {
  const colors = useColors();
  const { getAllCustomWidgets, getAllInventoryViews } = useAPI();
  const widgetContentRef = useRef(null);

  const customWidgetId = widget.id.startsWith("custom_")
    ? widget.id.replace("custom_", "")
    : widget.id;

  const { data: customWidgetsResponse, isLoading } = useQuery({
    queryKey: dashboardKeys.customWidgets(),
    queryFn: getAllCustomWidgets,

    select: (response) => response?.data || [],
  });

  const { data: inventoryViewsResponse } = useQuery({
    queryKey: inventoryViewKeys.all,
    queryFn: () => getAllInventoryViews(false),

    select: (response) => response?.data || [],
  });

  const inventoryViews = inventoryViewsResponse || [];

  const inventoryViewsMap = useMemo(() => {
    return inventoryViews.reduce((acc, view) => {
      acc[view.view_id] = view.name;
      return acc;
    }, {});
  }, [inventoryViews]);

  const customWidgets = customWidgetsResponse || [];
  const customWidget = customWidgets.find(
    (cw) => cw.custom_widget_id === customWidgetId
  );

  const chartData = useMemo(() => {
    if (!customWidget?.widget_latest_results) return [];

    const results = customWidget.widget_latest_results;
    if (typeof results !== "object") return [];

    const chartColors = [
      colors.secondary_600,
      colors.secondary_500,
      colors.secondary_400,
      colors.secondary_300,
      colors.secondary_200,
      colors.secondary_100,
      colors.primary_400,
      colors.primary_300,
      colors.primary_200,
      colors.primary_100,
      colors.primary_50,
      colors.primary_25,
    ];

    return results.map((result, index) => ({
      id: inventoryViewsMap[result.query_id] || result.query_id,
      label: inventoryViewsMap[result.query_id] || "-",
      value: typeof result.count === "number" ? result.count : 0,
      color: chartColors[index % chartColors.length],
    }));
  }, [customWidget?.widget_latest_results, inventoryViewsMap]);

  const info = useMemo(() => {
    const inventoryViewName = customWidget?.queries_to_run
      .map((queryId) => inventoryViewsMap[queryId])
      .join(", ");
    return `This widget shows the number of entities in the inventory view${
      inventoryViewName?.length > 1 ? "s: " : ": "
    } ${inventoryViewName}.`;
  }, [customWidget?.queries_to_run, inventoryViewsMap]);

  const widgetType = customWidget?.widget_type || "bar";
  const hasData = chartData.length > 0;

  const handleExportToPdf = useCallback(
    async (isCombinedExport = false) => {
      if (!widgetContentRef.current || !customWidget) return null;
      try {
        const container = await createPdfContainer();
        const header = createPdfHeader(widget.title || "Custom Widget");
        container.appendChild(header);
        const clone = widgetContentRef.current.cloneNode(true);
        clone.style.cssText = `
        background-color: white;
        padding: 24px;
        border: none;
        width: 100%;
        min-height: fit-content;
      `;
        container.appendChild(clone);
        if (isCombinedExport) {
          return container;
        }
        const { contentWidth, contentHeight } = await measureContentForPdf(
          container
        );
        const pdfConfig = createPdfConfig(
          generateFileName(
            widget.title || "Custom Widget",
            null,
            FILE_EXTENSIONS.PDF
          ),
          contentWidth,
          contentHeight
        );
        await html2pdf().set(pdfConfig).from(container).save();
        return null;
      } catch (error) {
        console.error("Failed to export custom widget PDF:", error);
        return null;
      }
    },
    [customWidget, widget.title]
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            p: 2,
          }}
        >
          <LoadingIcon size={24} />
        </Box>
      );
    }

    if (!customWidget) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            p: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: colors.text_secondary }}>
            Custom widget not found
          </Typography>
        </Box>
      );
    }

    if (!hasData) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 2,
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              backgroundColor: colors.grey_8percent,
              borderRadius: "8px",
            }}
          >
            <Typography variant="body2" sx={{ color: colors.text_secondary }}>
              No data available
            </Typography>
          </Box>
        </Box>
      );
    }

    if (widgetType === "bar") {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            p: 2,
            height: "100%",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              minHeight: "200px",
            }}
          >
            <BarChart
              xAxis={[
                {
                  scaleType: "band",
                  data: chartData.map((item) => item.label),
                  label: "Views",
                  colorMap: {
                    type: "ordinal",
                    colors: chartData.map((item) => item.color),
                  },
                },
              ]}
              series={[
                {
                  data: chartData.map((item) => item.value),
                },
              ]}
              slotProps={{
                legend: {
                  hidden: true,
                },
                tooltip: {
                  trigger: "item",
                },
                position: {
                  vertical: "top",
                  horizontal: "right",
                },
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                popper: {
                  sx: {
                    fontSize: 12,
                    fontFamily: "Poppins",
                    padding: "5px 10px",
                  },
                },
              }}
              height={300}
              margin={{ top: 20, right: 20, bottom: 60, left: 60 }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {chartData.map((item) => (
                <CustomLegendItem
                  key={item.id}
                  item={item}
                  highlightedItem={null}
                  onHover={() => {}}
                />
              ))}
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: 2,
          height: "100%",
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PieChart
            series={[
              {
                data: chartData,
                innerRadius: 0,
                outerRadius: 120,
                paddingAngle: 0.5,
                startAngle: -90,
                endAngle: 290,
                cornerRadius: 4,
              },
            ]}
            width={245}
            height={245}
            margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
            slotProps={{
              legend: {
                hidden: true,
              },
              tooltip: {
                trigger: "item",
              },
              position: {
                vertical: "top",
                horizontal: "right",
              },
              itemMarkWidth: 10,
              itemMarkHeight: 10,
              popper: {
                sx: {
                  fontSize: 12,
                  fontFamily: "Poppins",
                  padding: "5px 10px",
                },
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              gap: 1,
              flexWrap: "wrap",
              width: "20%",
            }}
          >
            {chartData.map((item) => (
              <CustomLegendItem
                key={item.name + item.id}
                item={item}
                highlightedItem={null}
                onHover={null}
              />
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  useImperativeHandle(
    ref,
    () => ({
      exportToPdf: (isCombinedExport = false) =>
        handleExportToPdf(isCombinedExport),
      widgetId: `custom-${customWidgetId}`,
      hasExportCapability: true,
    }),
    [customWidgetId, handleExportToPdf]
  );

  return (
    <WidgetWrapper
      title={widget.title}
      info={info}
      handleExportToPdf={() => handleExportToPdf(false)}
    >
      <Box ref={widgetContentRef}>
        <Typography
          variant="caption"
          sx={{ color: colors.text_secondary, pl: 2.5 }}
        >
          {customWidget?.description}
        </Typography>
        {renderContent()}
      </Box>
    </WidgetWrapper>
  );
});

CustomWidget.displayName = "CustomWidget";

export default CustomWidget;
